import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import { useTheme } from '@mui/material/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Grid from '@mui/material/Grid2'
import { useTranslation } from 'react-i18next'
import { IUrl } from 'types/cvsInterfaces'
import { IItemVisibility, ILayout } from 'types/layoutInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import VisibilityCheckbox from '../VisibilityCheckbox'
import colors from 'constants/colors'

interface IUrlProps {
  urls: IUrl[]
  layout: ILayout
  hideSection: (value: boolean, type: string) => void
  hideItem: (value: boolean, id: number, type: string) => void
  expanded: boolean
  setStatus: (value: boolean) => void
}

/**
 * Renders the Url component.
 *
 * @param {Object} urls - an array of URLs
 * @param {Object} layout - the layout configuration
 * @param {Function} hideSection - a function to hide the section
 * @param {Function} hideItem - a function to hide an item
 * @param {boolean} expanded - indicates if the component is expanded
 * @param {Function} setStatus - a function to set the status
 * @return {React.ReactElement} - the rendered Url component
 * @notExported
 */
const Url: React.FC<IUrlProps> = ({ urls, layout, hideSection, hideItem, expanded, setStatus }) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()

  let hidden = false
  if (layout && layout.ItemVisibilities && layout.ItemVisibilities.length > 0) {
    const visibilities = layout.ItemVisibilities.filter((visibility: IItemVisibility) => visibility.type === 'urls')
    if (visibilities.length > 0) {
      const visibilityMap = new Map(visibilities.map(item => [item.itemId, item.hidden]))
      hidden = urls.every(value => visibilityMap.get(value.id) === true)
    }
  }

  const Item: React.FC<{ url: IUrl }> = ({ url }) => {
    let hiddenItem: boolean | IItemVisibility = false
    if (layout && layout.ItemVisibilities && layout.ItemVisibilities.length > 0) {
      const visibilities = layout.ItemVisibilities.filter((visibility: IItemVisibility) => visibility.type === 'urls')
      if (visibilities.length > 0) {
        hiddenItem =
          visibilities.find((value: IItemVisibility) => value.itemId === url.id && value.hidden === true) ?? false
      }
    }

    return (
      <Box
        display="flex"
        flexDirection="row"
        flexWrap="nowrap"
        width="100%"
        my={2}
        sx={{
          padding: '2px',
          backgroundColor: theme.palette.background.default,
          '&:hover': {
            backgroundColor: theme.palette.background.paper,
            borderRadius: '5px',
          },
        }}
      >
        <Box width="100%">
          <Box display="flex" flexDirection="row" flexWrap="nowrap" width="100%" mb={1}>
            <VisibilityCheckbox
              checked={!!hiddenItem}
              onChange={(checked: boolean) => hideItem(checked, url.id, 'urls')}
            />
            <Box display="inline">{url.url}</Box>
          </Box>

          <Box display="flex" flexDirection="row" flexWrap="nowrap" width="100%" mb={1}></Box>
          <Box>{chooseDBTranslation(i18n, url).description}</Box>
        </Box>
      </Box>
    )
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, expanded) => setStatus(expanded)}
      elevation={0}
      sx={{ borderRadius: '5px', border: `1px solid ${colors.borderColor}` }}
      slotProps={{
        transition: { unmountOnExit: true },
      }}
    >
      <Box display="flex" alignItems="center">
        <VisibilityCheckbox checked={hidden} onChange={(checked: boolean) => hideSection(checked, 'urls')} />
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flex: 1,
            fontSize: '20px',
            color: theme.palette.primary.main,
            fontWeight: 'bold',
          }}
        >
          <Grid>{t(`print.layout.links`)}</Grid>
        </AccordionSummary>
      </Box>
      <AccordionDetails>
        <Box display="flex" flexDirection="column" flexWrap="wrap" width="100%">
          {urls.map((url: IUrl, index: number) => {
            return (
              <Box key={`url-${index}`}>
                <Item url={url} />
                {index + 1 < urls.length ? <Divider variant="fullWidth" /> : null}
              </Box>
            )
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

export default Url
